export function areObjectsEqual(
  objA: unknown,
  objB: unknown,
  options?: {
    exclude?: string[]
    exceptions?: Record<string, (a: unknown, b: unknown) => boolean>
  }
): boolean {
  if (objA === objB) {
    return true
  } // Same reference or primitive value
  if (typeof objA !== 'object' || typeof objB !== 'object' || null === objA || null === objB) {
    return false
  }
  const excludeSet = options?.exclude ? new window.Set(options.exclude) : null
  const filterFn = excludeSet ? (key: string) => !excludeSet.has(key) : (key: string) => key

  const keysA = Object.keys(objA).filter(filterFn)
  const keysB = Object.keys(objB).filter(filterFn)
  const setKeysB = new window.Set(keysB)

  if (keysA.length !== keysB.length) {
    return false
  }

  for (const key of keysA) {
    if (!setKeysB.has(key)) {
      return false
    }
    if (options?.exceptions?.[key]) {
      const exceptionResult = options.exceptions[key](
        (objA as unknown as Record<string, unknown>)[key],
        (objB as unknown as Record<string, unknown>)[key]
      )
      if (exceptionResult) {
        continue
      }
      return exceptionResult
    } else if (
      !areObjectsEqual(
        (objA as unknown as Record<string, unknown>)[key],
        (objB as unknown as Record<string, unknown>)[key],
        options
      )
    ) {
      return false
    }
  }

  return true
}

export function explainArrayDiff(
  arrA: unknown[],
  arrB: unknown[],
  options?: {
    maxItems?: number
  }
): string[] {
  const messages: string[] = []
  const max = options?.maxItems ?? 5

  if (arrA.length !== arrB.length) {
    messages.push(`Length mismatch: ${arrA.length} !== ${arrB.length}`)
  }

  const len = Math.min(arrA.length, arrB.length)

  for (let i = 0; i < len && messages.length < max; i++) {
    if (!areObjectsEqual(arrA[i], arrB[i])) {
      messages.push(`Index ${i} differs: ${JSON.stringify(arrA[i])} !== ${JSON.stringify(arrB[i])}`)
    }
  }

  if (arrA.length > arrB.length && messages.length < max) {
    messages.push(`Extra items in A starting at index ${arrB.length}: ${JSON.stringify(arrA.slice(arrB.length))}`)
  }

  if (arrB.length > arrA.length && messages.length < max) {
    messages.push(`Extra items in B starting at index ${arrA.length}: ${JSON.stringify(arrB.slice(arrA.length))}`)
  }

  return messages
}

export type PropsToTuples<T extends Record<string, unknown>> = (props: T) => T[keyof T][]

export function compareTuples<T extends Record<string, unknown>>(
  tuplesRule: PropsToTuples<T>
): (prev: T, curr: T) => boolean {
  return (prev: T, curr: T) => {
    const prevTuples = tuplesRule(prev)
    const currTuples = tuplesRule(curr)
    if (prevTuples.length !== currTuples.length) {
      return false
    }
    // eslint-disable-next-line unicorn/no-for-loop
    for (let i = 0; i < prevTuples.length; i++) {
      if (prevTuples[i] !== currTuples[i]) {
        return false
      }
    }
    return true
  }
}
